import axios from "axios";
import { sanitizeEncipherencode } from "./encipherencode";
import {
  getPhoneFromAuthKey,
  getRememberedTrackingPhone,
  getTrackingAuthKey,
  hasRememberedTrackingAuth,
  normalizeTrackingPhone,
  rememberTrackingPhoneConfirmation,
  restorePreviousAccessToken,
} from "./trackingAuth";
import {
  getCreateConflict,
  getIykaQuoteConflict,
} from "./returnRequestAvailability";
import { isV2Path } from "./routes";

const webhookUrl =
  "https://n8n.solidwebhook.com/webhook/b6a93ff1-3f43-4544-84a8-cc2d86d082d3";

const sanitizeWebhookPayload = (value, seen = new WeakSet()) => {
  if (value === null || value === undefined) return value;
  if (typeof value !== "object") return value;
  if (typeof FormData !== "undefined" && value instanceof FormData) {
    return "[FormData]";
  }
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }
  if (typeof File !== "undefined" && value instanceof File) {
    return {
      name: value.name,
      type: value.type,
      size: value.size,
    };
  }
  if (seen.has(value)) return "[Circular]";

  seen.add(value);

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeWebhookPayload(item, seen));
  }

  return Object.entries(value).reduce((acc, [key, item]) => {
    if (key === "__sentry_xhr_v3__") return acc;
    if (typeof item === "function") return acc;
    acc[key] = sanitizeWebhookPayload(item, seen);
    return acc;
  }, {});
};

export const sendWebhook = (data) => {
  try {
    fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(sanitizeWebhookPayload(data)),
    }).catch(() => {});
  } catch (e) {
    // ignore
  }
};

const instance = axios.create();

const getRequestBearerToken = (headers) => {
  const authorization =
    (typeof headers?.get === "function" && headers.get("Authorization")) ||
    headers?.Authorization ||
    headers?.authorization;
  const match = String(authorization || "").match(/^Bearer\s+(.+)$/i);

  return match?.[1]?.trim() || null;
};

const getDetailText = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail.map(getDetailText).filter(Boolean).join(", ");
  }
  if (typeof detail === "object") {
    return (
      getDetailText(detail.message) ||
      getDetailText(detail.detail) ||
      getDetailText(detail.error) ||
      getDetailText(detail.error_code_message) ||
      getDetailText(detail.error_code_title)
    );
  }
  return String(detail);
};

const isProductStockIssue = (webhookResponse, requestUrl) => {
  if (!requestUrl.includes("/api/v2/create")) return false;
  const detail =
    webhookResponse?.data?.detail ||
    webhookResponse?.data?.data?.detail ||
    webhookResponse?.detail;
  const errorCodeTitle =
    typeof detail === "object" && detail !== null
      ? detail.error_code_title
      : undefined;

  return getDetailText(errorCodeTitle).trim().toUpperCase() === "PRODUCT STOCK ISSUE";
};

instance.interceptors.request.use((config) => {
  const access_token = sessionStorage.getItem("access_token"); // 🔑 fetch dynamically
  if (access_token) {
    config.headers.Authorization = `Bearer ${access_token}`;
  }
  config.metadata = { startTime: Date.now() };
  return config;
});

instance.interceptors.response.use(
  (response) => {
    const duration =
      Date.now() - (response.config.metadata?.startTime || Date.now());
    if (
      response.config.url?.includes("/get/merchant/configurations") &&
      duration > 5000
    ) {
      sendWebhook({
        message: "configuration api taking more than 5 seconds",
        payload: response.config.params,
      });
    }
    return response;
  },
  (error) => {
    const payload = error.config
      ? error.config.data || error.config.params
      : null;
    const response = error?.response || { message: error.message };
    const extractedStatus = Number(
      error.message?.match(/status code (\d{3})/)?.[1]
    );
    const sentryXhrStatus = Number(
      error?.request?.__sentry_xhr_v3__?.status_code
    );
    const status =
      error?.response?.status ??
      (Number.isFinite(extractedStatus) ? extractedStatus : undefined) ??
      (Number.isFinite(sentryXhrStatus) ? sentryXhrStatus : undefined);
    const isBrowserNetworkFailure =
      status === 0 ||
      error?.request?.status === 0 ||
      error?.message === "Network Error";

    if (isBrowserNetworkFailure) {
      return Promise.reject(error);
    }

    if (
      status === 401 ||
      error?.response?.data?.detail === "Unauthorized" ||
      error?.response?.detail === "Unauthorized"
    ) {
      const requestToken = getRequestBearerToken(error?.config?.headers);
      const currentToken = sessionStorage.getItem("access_token");

      // A response can arrive after navigation restored or replaced the token.
      // It belongs to the displaced session and must not log out the current one.
      if (requestToken && requestToken !== currentToken) {
        return Promise.reject(error);
      }

      const params = new URLSearchParams(document.location.search);
      const storedQuery = sessionStorage.getItem("initialQueryParams") || "";
      const storedParams = new URLSearchParams(storedQuery);
      const retryFallbackPhone = normalizeTrackingPhone(
        error?.config?._trackingAuthFallbackPhone,
      );

      const authKey =
        getTrackingAuthKey(window.location.pathname, params) ||
        getTrackingAuthKey(window.location.pathname, storedParams);
      const isTrackingAuth = Boolean(
        authKey || hasRememberedTrackingAuth() || retryFallbackPhone,
      );
      const authKeyPhone =
        getPhoneFromAuthKey(authKey) ||
        getRememberedTrackingPhone() ||
        retryFallbackPhone;
      const fallbackPhone =
        normalizeTrackingPhone(params.get("p")) ||
        normalizeTrackingPhone(storedParams.get("p")) ||
        authKeyPhone;
      const phoneCameFromAuthClaim = Boolean(
        fallbackPhone &&
          fallbackPhone === authKeyPhone &&
          !normalizeTrackingPhone(params.get("p")) &&
          !normalizeTrackingPhone(storedParams.get("p")),
      );

      if (isTrackingAuth) {
        params.delete("auth_key");
        storedParams.delete("auth_key");
        const previousToken = restorePreviousAccessToken();

        if (previousToken) {
          if (authKeyPhone) {
            error.config._trackingAuthFallbackPhone = authKeyPhone;
          }

          const headers = error.config?.headers;
          if (typeof headers?.set === "function") {
            headers.set("Authorization", `Bearer ${previousToken}`);
          } else {
            error.config.headers = {
              ...headers,
              Authorization: `Bearer ${previousToken}`,
            };
          }

          return instance.request(error.config);
        }

        if (fallbackPhone) {
          params.set("p", fallbackPhone);
          if (phoneCameFromAuthClaim) {
            rememberTrackingPhoneConfirmation(fallbackPhone);
          }
        }
      }

      params.delete("auth_key");
      storedParams.delete("auth_key");

      storedParams.forEach((value, key) => {
        if (!params.has(key)) {
          params.set(key, value);
        }
      });

      let encipherencode = sanitizeEncipherencode(
        params.get("encipherencode") ||
          storedParams.get("encipherencode") ||
          sessionStorage.getItem("encipherencode")
      );

      if (encipherencode && !params.has("encipherencode")) {
        params.set("encipherencode", encipherencode);
      }

      const origin = window.location.origin;
      const queryString = params.toString();
      const loginPath = isV2Path(window.location.pathname) ? "/v2" : "/";

      sessionStorage.setItem("initialQueryParams", queryString);

      window.location.replace(
        `${origin}${loginPath}${queryString ? `?${queryString}` : ""}`
      );

      return Promise.reject(error);
    }
    const responseData = error.response?.data;
    const detailText =
      getDetailText(responseData?.detail) ||
      getDetailText(responseData?.error) ||
      getDetailText(responseData?.message) ||
      getDetailText(responseData);
    const requestUrl = error.config?.url || "";
    const orderNotDelivered =
      detailText === "Order Not Delivered!" &&
      requestUrl.includes("/order-status");
    const otpNoMatch =
      detailText.includes("OTP didn't match") &&
      requestUrl.includes("/verify_otp");
    const otpExpired =
      detailText.includes("OTP expired") &&
      requestUrl.includes("/verify_otp");
    const invalidMerchantId =
      requestUrl.includes("configurations") &&
      detailText.includes("not enabled");
    const productStockIssue = isProductStockIssue(response, requestUrl);
    const expectedCreateConflict =
      requestUrl.includes("/api/v2/create") &&
      (getCreateConflict(error) || getIykaQuoteConflict(error));
    if (
      !orderNotDelivered &&
      !otpNoMatch &&
      !invalidMerchantId &&
      !otpExpired &&
      !productStockIssue &&
      !expectedCreateConflict
    ) {
      const webhookData = {
        message: "api error",
        url: window.location.href,
        api_url: error.config && error.config.url,
        payload,
        response,
      };
      if (status >= 500) {
        webhookData.status = status;
        webhookData.stack = error.stack;
      }
      sendWebhook(webhookData);
    }
    return Promise.reject(error);
  }
);

export default instance;
